import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Download, ChartBar, Timer, Coffee, Warning, ArrowsClockwise, Funnel, Users, Briefcase, ListChecks, Calendar } from '@phosphor-icons/react';
import { usePagination } from '../components/TablePagination';
import { toDDMMYYYY } from '../lib/dateUtils';
import SearchableSelect from '../components/SearchableSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtHrs = (s) => {
  const n = Math.max(0, Math.floor(Number(s) || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const scoreOf = (row) => {
  const office = Number(row.check_in_seconds || 0);
  if (office <= 0) return null;
  return Math.min(100, Math.round((Number(row.tracked_seconds || 0) / office) * 100));
};
const scoreColor = (s) => {
  if (s === null || s === undefined) return { bg: '#F4F4F5', fg: '#6B7280', label: '—' };
  if (s >= 70) return { bg: '#dcfce7', fg: '#15803d', label: 'GOOD' };
  if (s >= 50) return { bg: '#fef3c7', fg: '#a16207', label: 'OK' };
  return { bg: '#fee2e2', fg: '#b91c1c', label: 'LOW' };
};

export const TimerProductivityPage = () => {
  const [from, setFrom] = useState(daysAgoISO(6));
  const [to, setTo] = useState(todayISO());
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [empId, setEmpId] = useState('');
  const [deptId, setDeptId] = useState('');
  const [data, setData] = useState({ rows: [], total_tracked_seconds: 0, total_idle_seconds: 0, total_break_overtime_seconds: 0, row_count: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/employees`, { withCredentials: true }).then((r) => setEmployees(r.data || [])).catch(() => {}),
      axios.get(`${API_URL}/api/departments`, { withCredentials: true }).then((r) => setDepartments(r.data || [])).catch(() => {}),
    ]);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (empId) {
        const emp = employees.find((e) => e.id === empId);
        if (emp?.user_id) params.user_id = emp.user_id;
      }
      if (deptId) params.department_id = deptId;
      const r = await axios.get(`${API_URL}/api/standup/timer/productivity`, { params, withCredentials: true });
      setData(r.data || {});
    } catch (e) {
      console.error('productivity fetch failed', e);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [from, to, empId, deptId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => (data.rows || []).map((r) => ({
    ...r,
    office_seconds: Number(r.check_in_seconds || 0),
    score: scoreOf(r),
  })), [data.rows]);

  const aggregate = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const k = r.employee_id || r.employee_name || '—';
      const a = map[k] = map[k] || { employee_id: r.employee_id, employee_name: r.employee_name, days: 0, office: 0, tracked: 0, brk: 0, brkOver: 0, idle: 0 };
      a.days += 1;
      a.office += r.office_seconds;
      a.tracked += r.tracked_seconds;
      a.brk += r.break_seconds || 0;
      a.brkOver += r.break_overtime_seconds || 0;
      a.idle += r.idle_seconds;
    });
    return Object.values(map).map((a) => ({
      ...a,
      score: a.office > 0 ? Math.min(100, Math.round((a.tracked / a.office) * 100)) : null,
    })).sort((x, y) => (y.tracked) - (x.tracked));
  }, [rows]);

  // Aggregate by CLIENT across all rows in the selected range.
  // Each row carries by_client = [{client, seconds}, ...] from the backend; we sum + track contributors.
  const byClient = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      (r.by_client || []).forEach((c) => {
        const k = c.client || '—';
        const entry = map[k] = map[k] || { client: k, seconds: 0, employees: new Set(), task_count: 0 };
        entry.seconds += Number(c.seconds || 0);
        if (r.employee_name) entry.employees.add(r.employee_name);
      });
      (r.by_task || []).forEach((t) => {
        if ((t.client_name || '—') in map) map[t.client_name || '—'].task_count += 1;
      });
    });
    return Object.values(map)
      .map((e) => ({ ...e, employees: Array.from(e.employees) }))
      .sort((a, b) => b.seconds - a.seconds);
  }, [rows]);

  // Aggregate by TASK across all rows. Group by (task_text + client_name) so the same task
  // for the same client across different days/employees rolls up into one row.
  const byTask = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      (r.by_task || []).forEach((t) => {
        const key = `${t.task_text || ''}||${t.client_name || '—'}`;
        const entry = map[key] = map[key] || {
          key,
          task_text: t.task_text || '(untitled)',
          client_name: t.client_name || '—',
          seconds: 0,
          employees: new Set(),
          last_status: t.status || '',
          sessions: 0,
        };
        entry.seconds += Number(t.seconds || 0);
        entry.sessions += 1;
        if (r.employee_name) entry.employees.add(r.employee_name);
        // Prefer running > paused > stopped for the status label
        const cur = entry.last_status, nxt = t.status || '';
        if (nxt === 'running' || (nxt === 'paused' && cur !== 'running')) entry.last_status = nxt;
      });
    });
    return Object.values(map)
      .map((e) => ({ ...e, employees: Array.from(e.employees) }))
      .sort((a, b) => b.seconds - a.seconds);
  }, [rows]);

  const [clientSearch, setClientSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [view, setView] = useState('employee');  // 'employee' | 'client' | 'task' | 'daily'
  const filteredByClient = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return byClient;
    return byClient.filter((c) => (c.client || '').toLowerCase().includes(q));
  }, [byClient, clientSearch]);
  const filteredByTask = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    if (!q) return byTask;
    return byTask.filter((t) =>
      (t.task_text || '').toLowerCase().includes(q)
      || (t.client_name || '').toLowerCase().includes(q)
    );
  }, [byTask, taskSearch]);

  const { paged, controls, startIdx } = usePagination(rows, { storageKey: 'tprod-page-size', testIdPrefix: 'tprod' });

  const exportCsv = () => {
    const header = ['#', 'Date', 'Employee', 'Office Hrs (s)', 'Tracked (s)', 'Break (s)', 'Break OT (s)', 'Idle (s)', 'Utilisation %'];
    const lines = [header.join(',')];
    rows.forEach((r, i) => lines.push([
      i + 1, r.date || '', (r.employee_name || '').replace(/,/g, ' '),
      Math.round(r.office_seconds), Math.round(r.tracked_seconds),
      Math.round(r.break_seconds || 0), Math.round(r.break_overtime_seconds || 0),
      Math.round(r.idle_seconds), r.score == null ? '' : r.score,
    ].join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Timer-Productivity-${from}-to-${to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 800);
  };

  const csvEscape = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const downloadCsv = (filename, header, rowsArr) => {
    const lines = [header.map(csvEscape).join(',')];
    rowsArr.forEach((row) => lines.push(row.map(csvEscape).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 800);
  };
  const exportClientsCsv = () => {
    downloadCsv(
      `Hours-by-Client-${from}-to-${to}.csv`,
      ['#', 'Client', 'Total Hours', 'Total Minutes', 'Total Seconds', 'Contributors', 'Employees'],
      filteredByClient.map((c, i) => [
        i + 1, c.client, (c.seconds / 3600).toFixed(2), Math.round(c.seconds / 60),
        Math.round(c.seconds), c.employees.length, c.employees.join('; '),
      ]),
    );
  };
  const exportTasksCsv = () => {
    downloadCsv(
      `Hours-by-Task-${from}-to-${to}.csv`,
      ['#', 'Task', 'Client', 'Total Hours', 'Total Minutes', 'Sessions', 'Employees', 'Status'],
      filteredByTask.map((t, i) => [
        i + 1, t.task_text, t.client_name, (t.seconds / 3600).toFixed(2),
        Math.round(t.seconds / 60), t.sessions, t.employees.join('; '), t.last_status,
      ]),
    );
  };

  return (
    <div data-testid="timer-productivity-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--dx-text)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1D9E75 0%, #047857 100%)' }}>
              <ChartBar size={18} weight="fill" className="text-white" />
            </div>
            Time Tracking
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
            How your team is spending time — by employee, by client, or by task. Data is from the Daily Standup task timers + attendance.
          </p>
        </div>
        <Button onClick={exportCsv} disabled={!rows.length} className="text-white" style={{ background: 'var(--dx-primary, #1D9E75)' }} data-testid="tprod-export-csv">
          <Download size={16} className="mr-2" /> Export Daily CSV
        </Button>
      </div>

      <Card className="p-4 mb-5" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', borderColor: '#e2e8f0' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: '#1D9E75' }}>
            <Funnel size={14} weight="fill" className="text-white" />
          </div>
          <div className="text-sm font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>Filters</div>
          <div className="text-[10px] text-gray-400 ml-1">Narrow the report by date range, team, or person</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1" data-testid="tprod-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1" data-testid="tprod-to" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Department</Label>
            <div className="mt-1">
              <SearchableSelect
                testId="tprod-dept"
                placeholder="All Departments"
                value={deptId}
                onChange={(v) => setDeptId(v || '')}
                options={[
                  { value: '', label: 'All Departments', sublabel: `${departments.length} total`, search: 'all departments' },
                  ...departments.map((d) => ({ value: d.id, label: d.name, search: d.name || '' })),
                ]}
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Employee</Label>
            <div className="mt-1">
              <SearchableSelect
                testId="tprod-employee"
                placeholder="All Employees"
                value={empId}
                onChange={(v) => setEmpId(v || '')}
                options={[
                  { value: '', label: 'All Employees', sublabel: `${employees.length} total`, search: 'all employees' },
                  ...employees.map((e) => ({
                    value: e.id,
                    label: e.name || '(unnamed)',
                    sublabel: e.emp_id || '',
                    search: `${e.name || ''} ${e.emp_id || ''} ${e.email || ''}`,
                  })),
                ]}
              />
            </div>
          </div>
          <div className="flex items-end">
            <Button onClick={fetchData} disabled={loading} className="w-full text-white" style={{ background: 'var(--dx-primary, #1D9E75)' }} data-testid="tprod-refresh">
              <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
        {(deptId || empId) && (
          <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap text-[11px]">
            <span className="text-gray-500 font-semibold uppercase tracking-wider">Active filters:</span>
            {deptId && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                Dept: {departments.find((d) => d.id === deptId)?.name || deptId}
                <button onClick={() => setDeptId('')} className="ml-0.5 hover:text-emerald-600" data-testid="clear-dept">×</button>
              </span>
            )}
            {empId && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200 font-semibold">
                Employee: {employees.find((e) => e.id === empId)?.name || empId}
                <button onClick={() => setEmpId('')} className="ml-0.5 hover:text-sky-600" data-testid="clear-emp">×</button>
              </span>
            )}
            <button
              onClick={() => { setDeptId(''); setEmpId(''); }}
              className="text-[10px] text-rose-600 hover:text-rose-700 font-bold underline"
              data-testid="clear-all-filters"
            >Clear all</button>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card className="p-3" style={{ borderLeft: '4px solid #1D9E75' }}>
          <div className="text-[10px] uppercase font-bold tracking-wide text-emerald-700 flex items-center gap-1"><Timer size={11} weight="fill" /> Total Tracked</div>
          <div className="text-2xl font-black mt-0.5 tabular-nums" data-testid="tprod-total-tracked">{fmtHrs(data.total_tracked_seconds)}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">{data.row_count} day-rows</div>
        </Card>
        <Card className="p-3" style={{ borderLeft: '4px solid #6B7280' }}>
          <div className="text-[10px] uppercase font-bold tracking-wide text-slate-700">Total Idle</div>
          <div className="text-2xl font-black mt-0.5 tabular-nums" data-testid="tprod-total-idle">{fmtHrs(data.total_idle_seconds)}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">Office hrs not covered by timers</div>
        </Card>
        <Card className="p-3" style={{ borderLeft: '4px solid #dc2626', background: data.total_break_overtime_seconds > 0 ? 'linear-gradient(135deg, #fff1f2 0%, white 60%)' : 'white' }}>
          <div className="text-[10px] uppercase font-bold tracking-wide text-rose-700 flex items-center gap-1"><Warning size={11} weight="fill" /> Break Overtime</div>
          <div className="text-2xl font-black mt-0.5 tabular-nums" style={{ color: data.total_break_overtime_seconds > 0 ? '#b91c1c' : 'var(--dx-text)' }} data-testid="tprod-total-break-ot">{fmtHrs(data.total_break_overtime_seconds)}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">Beyond allowed limits</div>
        </Card>
        <Card className="p-3" style={{ borderLeft: '4px solid #0ea5e9' }}>
          <div className="text-[10px] uppercase font-bold tracking-wide text-sky-700 flex items-center gap-1"><Coffee size={11} weight="fill" /> Avg Utilisation</div>
          <div className="text-2xl font-black mt-0.5 tabular-nums" data-testid="tprod-avg-score">
            {aggregate.length === 0 ? '—' : `${Math.round(aggregate.reduce((s, a) => s + (a.score || 0), 0) / Math.max(1, aggregate.filter((a) => a.score != null).length))}%`}
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">tracked ÷ office hrs</div>
        </Card>
      </div>

      {/* ─── VIEW TABS ────────────────────────────────────────────────── */}
      <div className="flex gap-1 mb-4 border-b" data-testid="time-tracking-tabs">
        {[
          { id: 'employee', label: 'By Employee', icon: Users, count: aggregate.length, hint: 'Per-person rollup with productivity score' },
          { id: 'client',   label: 'By Client',   icon: Briefcase, count: byClient.length,  hint: 'Total hours logged per client' },
          { id: 'task',     label: 'By Task',     icon: ListChecks, count: byTask.length,    hint: 'Individual tasks across clients' },
          { id: 'daily',    label: 'Daily Detail', icon: Calendar, count: rows.length,        hint: 'Day-by-day attendance + timers' },
        ].map((t) => {
          const Icon = t.icon;
          const active = view === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setView(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex-1 px-4 py-3 transition-all border-b-2 -mb-px text-left ${active
                ? 'border-emerald-500 bg-white'
                : 'border-transparent bg-transparent hover:bg-slate-50'}`}
            >
              <div className="flex items-center gap-2">
                <Icon size={16} weight={active ? 'fill' : 'regular'} className={active ? 'text-emerald-600' : 'text-slate-400'} />
                <div className={`text-sm font-bold ${active ? 'text-emerald-700' : 'text-slate-600'}`}>{t.label}</div>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {t.count}
                </span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5 hidden md:block">{t.hint}</div>
            </button>
          );
        })}
      </div>

      {view === 'employee' && (
        <Card className="mb-5 p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b text-xs font-bold uppercase tracking-wider text-gray-500 bg-slate-50">
            Per-Employee Roll-up ({from} → {to})
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ background: '#F8FAFC' }}>
                  <TableHead className="text-[10px] uppercase">Employee</TableHead>
                  <TableHead className="text-[10px] uppercase">Days</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Office</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Tracked</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Break</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Break OT</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Idle</TableHead>
                  <TableHead className="text-[10px] uppercase text-center">Utilisation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aggregate.map((a) => {
                  const col = scoreColor(a.score);
                  return (
                    <TableRow key={a.employee_id || a.employee_name} data-testid={`tprod-agg-${a.employee_id}`}>
                      <TableCell className="font-semibold">{a.employee_name || '—'}</TableCell>
                      <TableCell className="text-gray-500">{a.days}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtHrs(a.office)}</TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-700 font-semibold">{fmtHrs(a.tracked)}</TableCell>
                      <TableCell className="text-right tabular-nums text-amber-700">{fmtHrs(a.brk)}</TableCell>
                      <TableCell className="text-right tabular-nums font-bold" style={{ color: a.brkOver > 0 ? '#b91c1c' : '#9ca3af' }}>
                        {a.brkOver > 0 ? `+${fmtHrs(a.brkOver)}` : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-700">{fmtHrs(a.idle)}</TableCell>
                      <TableCell className="text-center">
                        <div className="inline-flex items-center gap-1.5">
                          <span className="text-xs font-black tabular-nums" style={{ color: col.fg }}>{a.score == null ? '—' : `${a.score}%`}</span>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: col.bg, color: col.fg }}>{col.label}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* ─── HOURS PER CLIENT ─── */}
      {view === 'client' && (
      <Card className="mb-5 p-0 overflow-hidden" data-testid="hours-by-client">
        <div className="px-4 py-2.5 border-b bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Hours per Client ({filteredByClient.length} client{filteredByClient.length !== 1 ? 's' : ''})
            </div>
            <div className="text-[10px] text-gray-400">Total time logged against each client across all employees in this range.</div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="search"
              placeholder="Search client…"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="h-8 text-xs w-44"
              data-testid="client-search"
            />
            <Button size="sm" variant="outline" onClick={exportClientsCsv} disabled={!filteredByClient.length} data-testid="export-clients-csv">
              <Download size={12} className="mr-1" /> CSV
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow style={{ background: '#F8FAFC' }}>
                <TableHead className="text-[10px] uppercase">#</TableHead>
                <TableHead className="text-[10px] uppercase">Client</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Total Hours</TableHead>
                <TableHead className="text-[10px] uppercase text-right">% of Tracked</TableHead>
                <TableHead className="text-[10px] uppercase text-center">Contributors</TableHead>
                <TableHead className="text-[10px] uppercase">Employees</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredByClient.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No client time logged in this range.</TableCell></TableRow>
              ) : filteredByClient.map((c, i) => {
                const totalTracked = data.total_tracked_seconds || 0;
                const pct = totalTracked > 0 ? Math.round((c.seconds / totalTracked) * 1000) / 10 : 0;
                return (
                  <TableRow key={c.client} data-testid={`client-row-${i}`}>
                    <TableCell className="text-gray-400">{i + 1}</TableCell>
                    <TableCell className="font-semibold">
                      {c.client === '—' ? (
                        <span className="italic text-gray-400" title="Timer started without selecting a client">No client selected</span>
                      ) : c.client}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700 font-bold">{fmtHrs(c.seconds)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <div className="inline-flex items-center gap-1.5">
                        <span className="text-xs font-semibold">{pct}%</span>
                        <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-gray-600 tabular-nums">{c.employees.length}</TableCell>
                    <TableCell className="text-[11px] text-gray-600">
                      <div className="flex flex-wrap gap-1">
                        {c.employees.slice(0, 5).map((e) => (
                          <span key={e} className="inline-block px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[10px] font-semibold border border-emerald-100">{e}</span>
                        ))}
                        {c.employees.length > 5 && (
                          <span className="text-[10px] text-gray-400">+{c.employees.length - 5} more</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
      )}

      {/* ─── HOURS PER TASK ─── */}
      {view === 'task' && (
      <Card className="mb-5 p-0 overflow-hidden" data-testid="hours-by-task">
        <div className="px-4 py-2.5 border-b bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Hours per Task ({filteredByTask.length} task{filteredByTask.length !== 1 ? 's' : ''})
            </div>
            <div className="text-[10px] text-gray-400">Total time spent on each individual task (grouped by task + client) across all employees.</div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="search"
              placeholder="Search task or client…"
              value={taskSearch}
              onChange={(e) => setTaskSearch(e.target.value)}
              className="h-8 text-xs w-52"
              data-testid="task-search"
            />
            <Button size="sm" variant="outline" onClick={exportTasksCsv} disabled={!filteredByTask.length} data-testid="export-tasks-csv">
              <Download size={12} className="mr-1" /> CSV
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow style={{ background: '#F8FAFC' }}>
                <TableHead className="text-[10px] uppercase">#</TableHead>
                <TableHead className="text-[10px] uppercase">Task</TableHead>
                <TableHead className="text-[10px] uppercase">Client</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Total Hours</TableHead>
                <TableHead className="text-[10px] uppercase text-center">Sessions</TableHead>
                <TableHead className="text-[10px] uppercase">Employees</TableHead>
                <TableHead className="text-[10px] uppercase text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredByTask.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">No tasks tracked in this range.</TableCell></TableRow>
              ) : filteredByTask.slice(0, 200).map((t, i) => {
                const statusBg = t.last_status === 'running' ? '#dcfce7'
                  : t.last_status === 'paused' ? '#fef3c7'
                  : t.last_status === 'stopped' ? '#e5e7eb' : '#f1f5f9';
                const statusFg = t.last_status === 'running' ? '#15803d'
                  : t.last_status === 'paused' ? '#a16207'
                  : t.last_status === 'stopped' ? '#374151' : '#475569';
                return (
                  <TableRow key={t.key} data-testid={`task-row-${i}`}>
                    <TableCell className="text-gray-400">{i + 1}</TableCell>
                    <TableCell className="font-medium max-w-[320px] truncate" title={t.task_text}>{t.task_text}</TableCell>
                    <TableCell className="text-[11px]">
                      {t.client_name === '—' ? <span className="italic text-gray-400">—</span> : t.client_name}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700 font-bold">{fmtHrs(t.seconds)}</TableCell>
                    <TableCell className="text-center tabular-nums text-gray-600">{t.sessions}</TableCell>
                    <TableCell className="text-[11px] text-gray-600">
                      <div className="flex flex-wrap gap-1">
                        {t.employees.slice(0, 3).map((e) => (
                          <span key={e} className="inline-block px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-800 text-[10px] font-semibold border border-sky-100">{e}</span>
                        ))}
                        {t.employees.length > 3 && (
                          <span className="text-[10px] text-gray-400">+{t.employees.length - 3}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase" style={{ background: statusBg, color: statusFg }}>
                        {t.last_status || '—'}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredByTask.length > 200 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-3 text-[11px] text-gray-400">
                    Showing top 200 of {filteredByTask.length}. Narrow with search or use CSV export for the full list.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      )}

      {view === 'daily' && (
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-2.5 border-b text-xs font-bold uppercase tracking-wider text-gray-500 bg-slate-50">
          Daily Breakdown ({rows.length} rows)
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F4F5]">
                <TableHead className="text-[10px] uppercase">#</TableHead>
                <TableHead className="text-[10px] uppercase">Date</TableHead>
                <TableHead className="text-[10px] uppercase">Employee</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Office</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Tracked</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Break</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Break OT</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Idle</TableHead>
                <TableHead className="text-[10px] uppercase">Top Clients</TableHead>
                <TableHead className="text-[10px] uppercase text-center">Utilisation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-400">Loading…</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10 text-[#52525B]">No data in this range. Ask team to start using the task timers on Daily Standup.</TableCell></TableRow>
              ) : paged.map((r, idx) => {
                const col = scoreColor(r.score);
                return (
                  <TableRow key={`${r.employee_id}-${r.date}`} data-testid={`tprod-row-${r.employee_id}-${r.date}`}>
                    <TableCell className="text-gray-400">{startIdx + idx + 1}</TableCell>
                    <TableCell>{toDDMMYYYY(r.date)}</TableCell>
                    <TableCell className="font-medium">{r.employee_name || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtHrs(r.office_seconds)}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700 font-semibold">{fmtHrs(r.tracked_seconds)}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-700">{fmtHrs(r.break_seconds || 0)}</TableCell>
                    <TableCell className="text-right tabular-nums font-bold" style={{ color: (r.break_overtime_seconds || 0) > 0 ? '#b91c1c' : '#9ca3af' }}>
                      {(r.break_overtime_seconds || 0) > 0 ? `+${fmtHrs(r.break_overtime_seconds)}` : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-700">{fmtHrs(r.idle_seconds)}</TableCell>
                    <TableCell className="text-[11px] text-gray-600">
                      {(r.by_client || []).slice(0, 2).map((c) => `${c.client} (${fmtHrs(c.seconds)})`).join(', ') || '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="inline-flex items-center gap-1.5">
                        <span className="text-xs font-black tabular-nums" style={{ color: col.fg }}>{r.score == null ? '—' : `${r.score}%`}</span>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: col.bg, color: col.fg }}>{col.label}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {controls}
      </Card>
      )}
    </div>
  );
};

export default TimerProductivityPage;
